import { Stack } from "@mui/material";
import { useMemo } from "react";

import { FeatureCreateOptionDraft } from "../../hooks/useDraftSelectedOption";
import { FeatureCreateOptionObservation } from "../types";
import { ObservationTypeTree } from "./components/ObservationTypeTree";
import { SpeciesAutocomplete } from "./components/SpeciesAutocomplete";
import { MapEditorFeatureCreateOptionsSelect_ObservationFragment } from "./fragments.generated";
import { buildDataMaps } from "./utils";

type Props = {
    researchTypes: MapEditorFeatureCreateOptionsSelect_ObservationFragment[];
    draftSelectedOption: Partial<FeatureCreateOptionObservation>;
    onUpdate: (option: FeatureCreateOptionDraft) => void;
};

export function ObservationSection({ researchTypes, draftSelectedOption, onUpdate }: Props) {
    const { allSpecies, observationTypeMap } = useMemo(() => buildDataMaps(researchTypes), [researchTypes]);

    const selectedSpecies = draftSelectedOption.species ?? null;

    const filteredResearchTypes = useMemo(
        () => (selectedSpecies ? researchTypes.filter(rt => rt.speciesV2.some(s => s.species.id === selectedSpecies.id)) : []),
        [researchTypes, selectedSpecies],
    );

    const selectedObservationTypeId = draftSelectedOption.observationType?.id ?? null;

    return (
        <Stack rowGap={2}>
            <SpeciesAutocomplete
                species={allSpecies}
                selectedSpecies={selectedSpecies}
                onSpeciesChange={species =>
                    onUpdate({
                        type: "observation",
                        species: species ?? undefined,
                    })
                }
            />
            {selectedSpecies && (
                <ObservationTypeTree
                    researchTypes={filteredResearchTypes}
                    selectedObservationTypeId={selectedObservationTypeId}
                    onSelectionChange={itemId => {
                        if (!itemId || !selectedSpecies) {
                            return;
                        }

                        const observationData = observationTypeMap.get(itemId);
                        if (!observationData) {
                            return;
                        }

                        const { observationType, researchType } = observationData;

                        onUpdate({
                            type: "observation",
                            species: selectedSpecies,
                            researchType: { id: researchType.id, name: researchType.name },
                            observationType,
                        });
                    }}
                />
            )}
        </Stack>
    );
}
